// API endpoints that require a live user session. All calls go through
// protectedFetch so a 401 triggers the session-expired flow once, globally.

import { protectedFetch } from './protectedFetch';
import type { BaseFetchOptions } from './baseFetch';
import {
  RoomDocument,
  RoomUser,
  SynthesizeSpeechRequest,
  UserAuthFields,
  UserFieldsUpdate,
  PoppyChatFeature,
  GenerateCommentaryRequest,
  GenerateCoverImageRequest,
  SaveCoverImageRequest,
  GenerateDevImageRequest,
  SaveDevImageRequest,
  LoonTransactionType,
  SaveWorldContentRequest,
  PublishWorldContentRequest,
  WorldPublishedResponse,
  WorldDraftResponse,
  WorldContentResponse,
  WorldContentHistoryResponse,
  PublishWorldContentResponse,
  AcknowledgeAchievementRequest,
} from '@airhead-games/shared/types';
import { getBaseUrl } from '@airhead-games/shared/utilities';

// WORLD
export function getPublishedWorld() {
  return protectedFetch<undefined, WorldPublishedResponse>(
    '/worlds/airhead-official/published',
    {
      method: 'GET',
      credentials: 'include',
    }
  );
}

export function getWorldDraft() {
  return protectedFetch<undefined, WorldDraftResponse>(
    '/worlds/airhead-official/draft',
    {
      method: 'GET',
      credentials: 'include',
    }
  );
}

export function getWorldContent(contentId: string) {
  return protectedFetch<undefined, WorldContentResponse>(
    `/worlds/airhead-official/content/${encodeURIComponent(contentId)}`,
    { method: 'GET', credentials: 'include' }
  );
}

export function getWorldContentHistory(options?: {
  limit?: number;
  cursor?: string;
}) {
  const params: Record<string, string> = {};
  if (typeof options?.limit === 'number') params.limit = String(options.limit);
  if (options?.cursor) params.cursor = options.cursor;

  return protectedFetch<undefined, WorldContentHistoryResponse>(
    '/worlds/airhead-official/history',
    {
      method: 'GET',
      params: Object.keys(params).length > 0 ? params : undefined,
      credentials: 'include',
    }
  );
}

export function saveWorldDraft(body: SaveWorldContentRequest) {
  return protectedFetch<SaveWorldContentRequest, WorldDraftResponse>(
    '/worlds/airhead-official/draft',
    {
      method: 'PUT',
      body,
      credentials: 'include',
    }
  );
}

export function publishWorldContent(body: PublishWorldContentRequest) {
  return protectedFetch<
    PublishWorldContentRequest,
    PublishWorldContentResponse
  >('/worlds/airhead-official/publish', {
    method: 'POST',
    body,
    credentials: 'include',
  });
}

// ROOMS
export function createRoom(host: RoomUser, gameSelected: string) {
  return protectedFetch<
    { host: RoomUser; gameSelected: string },
    { room: RoomDocument }
  >('/room-create', {
    method: 'POST',
    body: { host, gameSelected },
    credentials: 'include',
  });
}

// USERS
export function updateUser(
  update: Partial<UserFieldsUpdate>,
  options: Pick<BaseFetchOptions<Partial<UserFieldsUpdate>>, 'signal'> = {}
) {
  return protectedFetch<Partial<UserFieldsUpdate>, { user: UserAuthFields }>(
    '/user-update',
    {
      method: 'PATCH',
      body: update,
      credentials: 'include',
      signal: options.signal,
    }
  );
}

export interface UserLoonTransaction {
  _id: string;
  createdAt: Date | string;
  type: LoonTransactionType;
  delta: number;
  balanceAfter: number;
  gameId?: string | null;
  achievement?: { title: string; level: number | null } | null; // level is null on one-time tracks
}

export function getUserLoonTransactions(options?: {
  limit?: number;
  cursor?: string;
}) {
  const params: Record<string, string> = {};

  if (typeof options?.limit === 'number') {
    params.limit = String(options.limit);
  }

  if (options?.cursor) {
    params.cursor = options.cursor;
  }

  return protectedFetch<
    undefined,
    {
      transactions: UserLoonTransaction[];
      nextCursor: string | null;
    }
  >('/user-loon-transactions', {
    method: 'GET',
    params: Object.keys(params).length > 0 ? params : undefined,
    credentials: 'include',
  });
}

export function logout() {
  return protectedFetch<undefined, { success: true }>('/logout', {
    method: 'POST',
    credentials: 'include',
  });
}

// DEV TOOLS
export function generateCoverImage(body: GenerateCoverImageRequest) {
  return protectedFetch<
    GenerateCoverImageRequest,
    {
      requestId: string;
      candidates: Array<{
        index: number;
        base64: string;
        mimeType: string;
        sizeKb: number;
      }>;
    }
  >('/dev/generate-cover', {
    method: 'POST',
    body,
    credentials: 'include',
  });
}

export function saveCoverImage(body: SaveCoverImageRequest) {
  return protectedFetch<SaveCoverImageRequest, { fileName: string }>(
    '/dev/save-cover',
    {
      method: 'POST',
      body,
      credentials: 'include',
    }
  );
}

export function generateDevImage(body: GenerateDevImageRequest) {
  return protectedFetch<
    GenerateDevImageRequest,
    {
      requestId: string;
      candidates: Array<{
        index: number;
        base64: string;
        mimeType: string;
        sizeKb: number;
      }>;
    }
  >('/dev/generate-image', {
    method: 'POST',
    body,
    credentials: 'include',
  });
}

export function saveDevImage(body: SaveDevImageRequest) {
  return protectedFetch<SaveDevImageRequest, { fileName: string }>(
    '/dev/save-image',
    {
      method: 'POST',
      body,
      credentials: 'include',
    }
  );
}

// POPPY
export function generateCommentary(body: GenerateCommentaryRequest) {
  return protectedFetch<GenerateCommentaryRequest, { text: string }>(
    '/commentary',
    {
      method: 'POST',
      body,
      credentials: 'include',
    }
  );
}

// Does not use protectedFetch because it returns an ArrayBuffer.
// A 401 here won't notify the session policy, but synthesizeSpeech is only
// called from inside an active session, so the probe will have already fired.
export async function synthesizeSpeech(
  body: SynthesizeSpeechRequest
): Promise<ArrayBuffer> {
  const baseUrl = getBaseUrl();
  const res = await fetch(`${baseUrl}/api/speech`, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to synthesize speech');
  return await res.arrayBuffer();
}

// PRODUCTS
export function createCheckoutSession(
  stripePriceId: string,
  cancelPath?: string
) {
  return protectedFetch<
    { stripePriceId: string; cancelPath?: string },
    { url: string }
  >('/products/checkout-session', {
    method: 'POST',
    body: { stripePriceId, cancelPath },
    credentials: 'include',
  });
}

export function createBillingPortalSession(returnUrl: string) {
  return protectedFetch<{ returnUrl: string }, { url: string }>(
    '/products/billing-portal',
    {
      method: 'POST',
      body: { returnUrl },
      credentials: 'include',
    }
  );
}

export interface SavedPaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}

export function getSavedPaymentMethods() {
  return protectedFetch<
    undefined,
    {
      paymentMethods: SavedPaymentMethod[];
    }
  >('/products/payment-methods', {
    method: 'GET',
    credentials: 'include',
  });
}

export function quickPurchase(stripePriceId: string, paymentMethodId: string) {
  return protectedFetch<
    { stripePriceId: string; paymentMethodId: string },
    { status: 'succeeded' | 'fallback' }
  >('/products/quick-purchase', {
    method: 'POST',
    body: { stripePriceId, paymentMethodId },
    credentials: 'include',
  });
}

// TWITCH
export function initiateTwitchLink(options?: { redirectTo?: string }) {
  // This redirects to Twitch, so we use window.location.href instead of fetch
  const baseUrl = getBaseUrl();
  const url = new URL(`${baseUrl}/api/auth/twitch/link`);
  if (options?.redirectTo) {
    url.searchParams.set('redirectTo', options.redirectTo);
  }
  window.location.href = url.toString();
}

export function getTwitchStatus() {
  return protectedFetch<
    undefined,
    { isLinked: boolean; isValid: boolean; login?: string }
  >('/auth/twitch/status', {
    method: 'GET',
    credentials: 'include',
  });
}

export function initiateTwitchModScope(options?: { redirectTo?: string }) {
  // This redirects to Twitch, so we use window.location.href instead of fetch
  const baseUrl = getBaseUrl();
  const url = new URL(`${baseUrl}/api/auth/twitch/mod-scope`);
  if (options?.redirectTo) {
    url.searchParams.set('redirectTo', options.redirectTo);
  }
  window.location.href = url.toString();
}

/**
 * Start the Poppy chat grant. Redirects rather than fetches: the flow
 * ends on Twitch's consent screen, and may pass through two apps on the way.
 * The capability travels with it so only the switch the host touched comes back on.
 */
export function initiatePoppyChat(options?: {
  feature?: PoppyChatFeature;
  redirectTo?: string;
}) {
  const baseUrl = getBaseUrl();
  const url = new URL(`${baseUrl}/api/auth/twitch/poppy-chat`);
  if (options?.feature) {
    url.searchParams.set('feature', options.feature);
  }
  if (options?.redirectTo) {
    url.searchParams.set('redirectTo', options.redirectTo);
  }
  window.location.href = url.toString();
}

/**
 * Ask the server to settle the Poppy chat grant against Twitch. Called
 * when the panel opens, so a host who disconnected the app on Twitch sees the
 * switches go off rather than a stale "on".
 */
export function checkPoppyChatAuth() {
  return protectedFetch<undefined, { authorized: boolean }>(
    '/auth/twitch/poppy-chat/status',
    { method: 'GET', credentials: 'include' }
  );
}

export function syncBlockedUsers() {
  return protectedFetch<
    undefined,
    {
      success: boolean;
      user?: UserAuthFields;
      error?: string;
    }
  >('/auth/twitch/sync-blocked-users', {
    method: 'POST',
    credentials: 'include',
  });
}

export function syncBlockedTerms() {
  return protectedFetch<
    undefined,
    {
      success: boolean;
      user?: UserAuthFields;
      error?: string;
    }
  >('/auth/twitch/sync-blocked-terms', {
    method: 'POST',
    credentials: 'include',
  });
}

// ACHIEVEMENTS
// Marks one completion as shown after its celebration. Idempotent.
export function acknowledgeAchievement(body: AcknowledgeAchievementRequest) {
  return protectedFetch<
    AcknowledgeAchievementRequest,
    { acknowledged: boolean }
  >('/achievements/acknowledge', {
    method: 'POST',
    body,
    credentials: 'include',
  });
}
